import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { graphql, Link } from 'gatsby'
import {
  Box,
  Typography,
  Paper,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from '@material-ui/core'
import styled from 'styled-components'
import {
  CardGiftcard,
  Person,
  MailOutline,
  AttachMoney,
} from '@material-ui/icons'
import { useTheme } from '@material-ui/styles'
import ContextConsumer from '../components/Context'
import { saveUser, removeUser } from '../utils/localStorage'
import BgLogo from '../components/BgLogo'

const LoginUser = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  background-color: ${props => props.theme.palette.primary.main};
  color: ${props => props.color};
`
const Logo = styled(Link)`
  color: inherit;
  text-decoration: 'none';
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing(12)}px;
  margin-bottom: ${props => props.theme.spacing(12)}px;
  height: 60px;
`
const UserSelect = styled(FormControl)`
  min-width: 200px;
  margin: 0 auto ${props => props.theme.spacing(12)}px;

  .MuiOutlinedInput-root,
  .MuiInputBase-root,
  .MuiSelect-icon,
  .MuiInputLabel-root {
    color: inherit;
  }

  .MuiOutlinedInput-root {
    &:hover {
      .MuiOutlinedInput-notchedOutline {
        border-color: inherit;
        opacity: 1;
      }
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: inherit;
    opacity: 0.54;
  }

  .Mui-focused {
    .MuiOutlinedInput-notchedOutline {
      border-width: 1px;
      border-color: inherit;
      opacity: 1;
    }
  }
`
const UserTitle = styled(Typography)`
  margin-bottom: ${props => props.theme.spacing(6)}px;
  font-size: 1.25rem;
  line-height: 2rem;
`
const UserDetails = styled(Paper)`
  padding: ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(5)}px;
  margin-bottom: ${props => props.theme.spacing(8)}px;
  text-align: left;
  box-shadow: 0px 4px 6px 2px rgba(0, 0, 0, 0.12),
    0px 4px 4px 0px rgba(0, 0, 0, 0.14), 0px 2px 10px 0px rgba(0, 0, 0, 0.2);
`
const UserDetailsTitle = styled.div`
  font-size: 1.125rem;
  color: ${props => props.theme.palette.primary.main};
  margin-bottom: ${props => props.theme.spacing(4)}px;
`
const UserDetail = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-content: center;
  padding ${props => props.theme.spacing(1)}px 0 ;
`
const UserDetailLabel = styled.div`
  width: 100px;
  margin-right: ${props => props.theme.spacing(4)}px;
  font-size: 0.778rem;
  color: ${props => props.theme.palette.primary.main};
`
const UserDetailValue = styled.div`
  font-size: 0.778rem;
  color: ${props => props.theme.palette.text.secondary};
`

const UserPage = ({ data: file }) => {
  const { edges } = file.allUsersJson
  // Bring in theme to get contrast text value
  const theme = useTheme()
  const contrastText = theme.palette.getContrastText(theme.palette.primary.main)

  // Required to calculate elect label width
  const inputLabel = useRef(null)
  const [labelWidth, setLabelWidth] = useState(0)
  useEffect(() => {
    setLabelWidth(inputLabel.current.offsetWidth)
  }, [])

  // Local state to manage select vales
  const [username, setUsername] = useState('')

  return (
    <ContextConsumer>
      {({ data, set }) => {
        const hasSelectedUser = Object.keys(data.user).length > 0

        const updateUser = event => {
          const { value } = event.target
          console.log(value)

          if (value === `logout`) {
            set({ user: {} })
            removeUser()
          } else {
            const { node } = edges[value]
            set({ user: node })
            // let's save the user to local storage
            saveUser(node)
          }
          setUsername(value)
        }

        return (
          <>
            <LoginUser color={contrastText}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Logo to="/">
                  <BgLogo viewBox="0 0 1247.1 321.9" height={80}></BgLogo>
                </Logo>
                <UserTitle color="inherit">Select User</UserTitle>
                <UserSelect variant="outlined">
                  <InputLabel htmlFor="user" ref={inputLabel}>
                    User
                  </InputLabel>
                  <Select
                    value={username}
                    onChange={updateUser}
                    labelWidth={labelWidth}
                    inputProps={{
                      name: 'user',
                      id: 'user',
                    }}
                  >
                    <MenuItem value="logout">
                      <em>Not selected</em>
                    </MenuItem>
                    {edges.map(({ node }, key) => (
                      <MenuItem key={key} value={key}>
                        {node.name}
                      </MenuItem>
                    ))}
                  </Select>
                </UserSelect>
                {hasSelectedUser && (
                  <>
                    <UserDetails>
                      <UserDetailsTitle>Account Details</UserDetailsTitle>
                      <UserDetail>
                        <UserDetailLabel>
                          <Person
                            fontSize="small"
                            style={{ marginRight: '8px', marginBottom: '-6px' }}
                          />
                          Name:
                        </UserDetailLabel>
                        <UserDetailValue>{data.user.name}</UserDetailValue>
                      </UserDetail>
                      <UserDetail>
                        <UserDetailLabel>
                          <MailOutline
                            fontSize="small"
                            style={{ marginRight: '8px', marginBottom: '-6px' }}
                          />
                          Email:
                        </UserDetailLabel>
                        <UserDetailValue>{data.user.email}</UserDetailValue>
                      </UserDetail>
                      <UserDetail>
                        <UserDetailLabel>
                          <AttachMoney
                            fontSize="small"
                            style={{ marginRight: '8px', marginBottom: '-6px' }}
                          />
                          Balance:
                        </UserDetailLabel>
                        <UserDetailValue>{data.user.balance}</UserDetailValue>
                      </UserDetail>
                      {data.user.freebet && (
                        <UserDetail>
                          <UserDetailLabel>
                            <CardGiftcard
                              fontSize="small"
                              style={{
                                marginRight: '8px',
                                marginBottom: '-6px',
                              }}
                            />
                            Free Bet:
                          </UserDetailLabel>
                          <UserDetailValue>Available</UserDetailValue>
                        </UserDetail>
                      )}
                    </UserDetails>
                  </>
                )}
              </Box>
            </LoginUser>
          </>
        )
      }}
    </ContextConsumer>
  )
}

UserPage.propTypes = {
  data: PropTypes.object.isRequired,
}

export const userQuery = graphql`
  query userQuery {
    allUsersJson {
      edges {
        node {
          id
          name
          balance
          age
          email
          freebet
          history {
            id
            buttonIndex
            competition
            date
            marketIndex
            marketTitle
            name
            odds
            returns
            slug
            sport
            stake
            start
            status
            betKey
          }
        }
      }
    }
  }
`
export default UserPage
